import React from 'react';
import {
  ADMIN_PROSPECT_UNIT_MIX_KEYS,
  adminProspectUnitMixLabel,
  formatAdminProspectUnits,
} from './adminProspectDisplay';
import {
  ProspectOwnerResponseCard,
  ProspectOwnerResponseField,
  ProspectProjectionProvenance,
  ProspectProjectionVerification,
} from './ProspectOwnerResponseShared';

export default function ProspectOwnerProvidedPropertyCard({
  profile,
  language,
  t,
  busy,
  pending,
  error,
  onRequestChange,
  onConfirm,
  onCancel,
  onReload,
  hideTitle = false,
  embedded = false,
}) {
  if (!profile) {
    return (
      <ProspectOwnerResponseCard
        title={t('admin.prospection.section.ownerProvidedBuilding')}
        hideTitle={hideTitle}
        embedded={embedded}
      >
        <p className="text-sm text-slate-600">{t('admin.prospection.ownerResponse.building.empty')}</p>
      </ProspectOwnerResponseCard>
    );
  }

  const mismatch = profile.unitCountMismatchConfirmed === true;
  const constructionYear = profile.constructionYearUnknown
    ? t('admin.prospection.ownerResponse.constructionYearUnknown')
    : profile.constructionYear != null
      ? String(profile.constructionYear)
      : null;
  const unitMixEntries = ADMIN_PROSPECT_UNIT_MIX_KEYS.map((key) => {
    const label = adminProspectUnitMixLabel(key, t);
    if (!label || profile.unitMix?.[key] == null) return null;
    return { key, label, value: formatAdminProspectUnits(profile.unitMix[key]) };
  }).filter(Boolean);

  return (
    <ProspectOwnerResponseCard
      title={t('admin.prospection.section.ownerProvidedBuilding')}
      description={t('admin.prospection.ownerResponse.targetingUnitsNote')}
      hideTitle={hideTitle}
      embedded={embedded}
      fields={
        <>
          {constructionYear ? (
            <ProspectOwnerResponseField label={t('admin.prospection.ownerResponse.constructionYear')}>
              {constructionYear}
            </ProspectOwnerResponseField>
          ) : null}
          {profile.declaredUnitTotal != null ? (
            <ProspectOwnerResponseField label={t('admin.prospection.ownerResponse.declaredUnits')}>
              {formatAdminProspectUnits(profile.declaredUnitTotal)}
            </ProspectOwnerResponseField>
          ) : null}
          {profile.importedNumberOfUnits != null ? (
            <ProspectOwnerResponseField label={t('admin.prospection.ownerResponse.importedUnits')}>
              {formatAdminProspectUnits(profile.importedNumberOfUnits)}
            </ProspectOwnerResponseField>
          ) : null}
          {mismatch || (profile.declaredUnitTotal != null && profile.importedNumberOfUnits != null) ? (
            <ProspectOwnerResponseField label={t('admin.prospection.ownerResponse.unitMismatchLabel')}>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${
                  mismatch ? 'bg-amber-50 text-amber-950 ring-amber-200' : 'bg-slate-100 text-slate-800 ring-slate-200'
                }`}
              >
                {mismatch
                  ? t('admin.prospection.ownerResponse.unitMismatch')
                  : t('admin.prospection.ownerResponse.unitMismatchOk')}
              </span>
            </ProspectOwnerResponseField>
          ) : null}
          <ProspectProjectionProvenance provenance={profile.provenance} language={language} t={t} />
        </>
      }
    >
      {unitMixEntries.length ? (
        <section aria-labelledby="prospect-owner-unit-mix">
          <h3
            id="prospect-owner-unit-mix"
            className="text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            {t('admin.prospection.ownerResponse.unitMix')}
          </h3>
          <dl className="mt-2 grid gap-2 sm:grid-cols-2">
            {unitMixEntries.map((entry) => (
              <ProspectOwnerResponseField key={entry.key} label={entry.label}>
                {entry.value}
              </ProspectOwnerResponseField>
            ))}
          </dl>
        </section>
      ) : null}
      <ProspectProjectionVerification
        section="latestOwnerProvidedPropertyProfile"
        verificationState={profile.verificationState}
        busy={busy}
        pending={pending}
        error={error}
        t={t}
        onRequestChange={onRequestChange}
        onConfirm={onConfirm}
        onCancel={onCancel}
        onReload={onReload}
      />
    </ProspectOwnerResponseCard>
  );
}
